import React, { useState, useEffect } from 'react';
import { coins } from '../utils/api';
import { useEscapeKey } from '../hooks/useEscapeKey';

const TAILWIND_COLORS = [
  { name: 'red', label: 'Red' },
  { name: 'yellow', label: 'Yellow' },
  { name: 'green', label: 'Green' },
  { name: 'blue', label: 'Blue' },
  { name: 'indigo', label: 'Indigo' },
  { name: 'purple', label: 'Purple' },
  { name: 'pink', label: 'Pink' },
  { name: 'gray', label: 'Gray' }
];

const INTENSITIES = ['100', '200', '300', '400', '500', '600', '700', '800'];

// Helper function to determine text color based on background intensity
const getTextColor = (intensity) => {
  const intensityNum = parseInt(intensity);
  return intensityNum >= 500 ? 'text-white' : 'text-gray-900';
};

export default function CustomStatusModal({ isOpen, onClose, onStatusCreated, onStatusUpdated, editStatus }) {
  const [name, setName] = useState('');
  const [selectedColor, setSelectedColor] = useState('blue-500');
  const [error, setError] = useState('');

  const handleClose = () => {
    setName('');
    setSelectedColor('blue-500');
    setError('');
    onClose();
  };

  useEscapeKey(handleClose, isOpen);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    try {
      if (editStatus) {
        const result = await coins.updateCustomStatus(editStatus.id, {
          name: name.trim(),
          color: selectedColor
        });
        onStatusUpdated(result);
      } else {
        const result = await coins.createCustomStatus({
          name: name.trim(),
          color: selectedColor
        });
        onStatusCreated(result);
      }
      onClose();
    } catch (err) {
      setError(err.response?.data?.error || `Failed to ${editStatus ? 'update' : 'create'} custom status`);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
      <div className="relative top-20 mx-auto p-5 border w-[95%] max-w-md shadow-lg rounded-md bg-white">
        <div className="mt-3">
          <h3 className="text-lg font-medium leading-6 text-gray-900">
            {editStatus ? 'Edit Custom Status' : 'Create Custom Status'}
          </h3>
          <form onSubmit={handleSubmit} className="mt-4">
            <div className="mb-4">
              <label htmlFor="name" className="block text-sm font-medium text-gray-700">
                Status Name
              </label>
              <input
                type="text"
                id="name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                required
              />
            </div>

            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Preview
              </label>
              <div className="mb-3">
                <span className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium bg-${selectedColor.split('-')[0]}-${selectedColor.split('-')[1]} ${getTextColor(selectedColor.split('-')[1])}`}>
                  {name || 'Status Preview'}
                </span>
              </div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Color
              </label>
              <div className="grid grid-cols-4 sm:grid-cols-8 gap-3">
                {TAILWIND_COLORS.map(color => (
                  <div key={color.name} className="relative">
                    <button
                      type="button"
                      className={`w-8 h-8 sm:w-6 sm:h-6 rounded-full bg-${color.name}-500 ${getTextColor('500')} ${selectedColor.startsWith(color.name) ? 'ring-2 ring-offset-2' : 'hover:ring-2 hover:ring-offset-2'} ring-${color.name}-500`}
                      onClick={() => setSelectedColor(`${color.name}-500`)}
                      title={color.label}
                    />
                  </div>
                ))}
              </div>
              <div className="mt-3 grid grid-cols-4 sm:grid-cols-8 gap-3">
                {INTENSITIES.map(intensity => (
                  <div key={intensity} className="relative">
                    <button
                      type="button"
                      className={`w-8 h-8 sm:w-6 sm:h-6 rounded-full bg-${selectedColor.split('-')[0]}-${intensity} ${getTextColor(intensity)} ${selectedColor.endsWith(intensity) ? 'ring-2 ring-offset-2' : 'hover:ring-2 hover:ring-offset-2'} ring-${selectedColor.split('-')[0]}-${intensity}`}
                      onClick={() => setSelectedColor(`${selectedColor.split('-')[0]}-${intensity}`)}
                      title={`Intensity ${intensity}`}
                    />
                  </div>
                ))}
              </div>
            </div>

            {error && (
              <div className="mb-4 text-sm text-red-600">
                {error}
              </div>
            )}

            <div className="flex flex-col sm:flex-row justify-end gap-3 mt-6">
              <button
                type="button"
                onClick={handleClose}
                className="w-full sm:w-auto px-4 py-2.5 sm:py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
              >
                Cancel
              </button>
              <button
                type="submit"
                className="w-full sm:w-auto px-4 py-2.5 sm:py-2 text-sm font-medium text-white bg-indigo-600 border border-transparent rounded-md hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
              >
                {editStatus ? 'Save Changes' : 'Create'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}
